import React from 'react';
import { Card, CardContent } from '@/components/shared/card';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, X } from 'lucide-react';
import AddPeerButton from './AddPeerButton';
import { formatPlaceName } from '@/lib/utils';
import { Place } from '@/types';

interface PeersCardProps {
  peers: Place[];
  isLoading: boolean;
  error: string | null;
  onRemovePeer: (placeFips: string) => void;
  onAddPeer: (peer: Place) => void;
  selectedPlace: Place | null;
}

const PeersCard: React.FC<PeersCardProps> = ({ peers, isLoading, error, onRemovePeer, onAddPeer, selectedPlace }) => {
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-24">
          <Loader2 className="animate-spin h-8 w-8 text-gray-500" />
          <p className="ml-2">Finding Peer Cities...</p>
        </div>
      );
    }

    if (error) {
      return <p className="text-red-500">{error}</p>;
    }

    const totalSlots = 5;
    const placeholderCount = Math.max(0, totalSlots - peers.length);

    return (
      <div className="grid grid-cols-1 grid-rows-5 gap-3 md:grid-cols-5 md:grid-rows-1">
        <AnimatePresence>
          {peers.map((peer) => (
            <motion.div
              key={peer.place_fips}
              layout
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="group relative flex items-center justify-center p-2 border rounded-md text-center bg-gray-50 text-sm cursor-pointer"
            >
              <span className="transition-opacity group-hover:opacity-0">{formatPlaceName(peer.name)}</span>
              <button
                onClick={() => onRemovePeer(peer.place_fips)}
                className="absolute inset-0 flex items-center justify-center bg-[#ca0270] text-white rounded-md opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                aria-label={`Remove ${peer.name}`}
              >
                <X size={14} className="mr-1" />
                Remove This Peer
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
        {Array.from({ length: placeholderCount }).map((_, index) => (
          <motion.div layout transition={{ type: 'spring', stiffness: 300, damping: 30 }} key={`add-peer-${index}`}>
            <AddPeerButton onPeerSelected={onAddPeer} selectedPlace={selectedPlace} />
          </motion.div>
        ))}
      </div>
    );
  };

  return (
    <Card className="border-[#ca0270]">
      <CardContent className="pt-6">
        {renderContent()}
      </CardContent>
    </Card>
  );
};

export default PeersCard; 